import {
  closestCorners,
  defaultDropAnimationSideEffects,
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useNavigate, useParams } from 'react-router-dom';
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react';
import backButtonDark from '@/assets/svg/static/back-button-dark.svg';
import DoubleColumnProductList from '@/components/features/DoubleColumnProductList/DoubleColumnProductList';
import ImageUploadZone from '@/components/features/ImageUploadZone/ImageUploadZone';
import { ProductCard } from '@/components/ui';
import {
  MAKRKETPLACE_IMAGE_MAP,
  type MarketplaceKey,
} from '@/constants/marketplaceImage';
import { useUploadBatchListener } from '@/hooks/useUploadBatchListener';
import { getUploadedImageUrls } from '@/services/firebase';
import { uploadImages } from '@/services/slaydAPI';
import {
  createCuration,
  fetchCurationById,
  updateCuration,
} from '@/services/slaydAPI/curations';
import {
  fetchInternalCurationFilters,
  fetchSimilarProducts,
  generateSimilarProductsFeed,
  type GenerateSimilarProductsFeedParams,
  type InternalCurationFilterOption,
} from '@/services/slaydAPI/products';
import useCreateCurationStore, {
  CurationPhase,
} from '@/stores/CreateCurationStore';
import type { Product } from '@/types/Product.types';
import { updatedImageUrl } from '@/utils/formatting';
import {
  PHASE_LABELS,
  PHASE_ORDER,
  REPLACEMENT_HISTORY_SAVE_DEBOUNCE_MS,
  type DraggableProductItemProps,
} from './CreateCurationPage.constants';
import styles from './CreateCurationPage.module.css';

// Module-level cache for filter options — fetched once per session
let cachedFilterOptions: {
  brands: InternalCurationFilterOption[];
  marketplaces: InternalCurationFilterOption[];
} | null = null;

const DraggableProductItem = memo(
  ({
    product,
    isReplacing,
    hasHistory,
    historyCount,
    rank,
    isSelected,
    isInDragGroup,
    onRestore,
    onReplace,
    onRemove,
    onToggleSelection,
  }: DraggableProductItemProps): JSX.Element => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: product.id });
    const groupGhost = isInDragGroup && !isDragging;
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : groupGhost ? 0.45 : 1,
    };
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`${styles.productWrapper} ${isDragging ? styles.isDragging : ''} ${groupGhost ? styles.groupGhost : ''}`}
      >
        <div {...attributes} {...listeners} className={styles.dragHandle}>
          <ProductCard product={product} disableNavigation={true} />
        </div>
        {hasHistory && (
          <button
            type="button"
            className={styles.restoreButton}
            title={`Restore previous (${historyCount})`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRestore(product);
            }}
          >
            <span className={styles.restoreIcon}>↶</span>
            {historyCount > 1 && (
              <span className={styles.restoreCount}>{historyCount}</span>
            )}
          </button>
        )}
        <button
          type="button"
          className={styles.replaceButton}
          title="Replace with similar product"
          disabled={isReplacing}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onReplace(product);
          }}
        >
          {isReplacing ? <div className={styles.miniSpinner} /> : '🔄'}
        </button>
        <button
          type="button"
          className={styles.removeButton}
          title="Remove from Curation"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove(product.id);
          }}
        >
          ×
        </button>
        <button
          type="button"
          className={`${styles.selectionButton} ${isSelected ? styles.selected : ''}`}
          title={
            isSelected
              ? 'Deselect (or drag any selected to drop the group anywhere)'
              : 'Select — then move to top or drag to reorder as a group'
          }
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleSelection(product.id);
          }}
        >
          {isSelected ? rank : '+'}
        </button>
      </div>
    );
  },
  // Product fields (price, image, etc.) are not deep-compared: catalog rows are treated as
  // immutable snapshots for this session. Reference equality re-renders when the store
  // swaps in a new object (e.g. replace); same id + same reference skips redundant work.
  (prev, next) =>
    prev.product === next.product &&
    prev.isReplacing === next.isReplacing &&
    prev.hasHistory === next.hasHistory &&
    prev.historyCount === next.historyCount &&
    prev.rank === next.rank &&
    prev.isSelected === next.isSelected &&
    prev.isInDragGroup === next.isInDragGroup
);
DraggableProductItem.displayName = 'DraggableProductItem';

const CreateCurationPage = (): JSX.Element => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEditMode = !!id;
  const { batches } = useUploadBatchListener();

  const {
    currentPhase,
    setPhase,
    uploadedImages,
    addImages,
    removeImage,
    updateImageUploadProgress,
    updateImageUrl,
    sale,
    setSale,
    filters,
    setFilters,
    generatedFeed,
    setGeneratedFeed,
    appendToFeed,
    clearFeed,
    pagination,
    setPagination,
    selectedProducts,
    setSelectedProducts,
    toggleProductSelection,
    isProductSelected,
    reviewProducts,
    setReviewProducts,
    removeReviewProduct,
    replaceReviewProduct,
    existingProducts,
    setExistingProducts,
    initReviewProducts,
    metadata,
    updateMetadata,
    isGeneratingFeed,
    setIsGeneratingFeed,
    isSubmitting,
    setIsSubmitting,
    error,
    setError,
    reset,
  } = useCreateCurationStore();

  const currentPhaseIndex = PHASE_ORDER.indexOf(currentPhase);

  // ── Local state ────────────────────────────────────────────────────────────
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [batchUploadInfo, setBatchUploadInfo] = useState<{
    documentId: string;
    totalCount: number;
  } | null>(null);
  const [replacingProductId, setReplacingProductId] = useState<number | null>(
    null
  );
  const [usedReplacements, setUsedReplacements] = useState<
    Map<number, Set<number>>
  >(new Map());
  const [replacementHistory, setReplacementHistory] = useState<
    Record<number, Product[]>
  >({});
  const [selectedRanks, setSelectedRanks] = useState<Map<number, number>>(
    new Map()
  );
  const [activeDragId, setActiveDragId] = useState<number | null>(null);
  const [filterOptions, setFilterOptions] = useState<{
    brands: InternalCurationFilterOption[];
    marketplaces: InternalCurationFilterOption[];
  }>({ brands: [], marketplaces: [] });
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [importFromExistingCuration, setImportFromExistingCuration] =
    useState(false);
  const [existingCurationId, setExistingCurationId] = useState<string>('');
  const [isImportingProducts, setIsImportingProducts] =
    useState<boolean>(false);
  const [importPreview, setImportPreview] = useState<{
    products: Product[];
    target: 'select' | 'review';
  } | null>(null);
  const [importSelectedIds, setImportSelectedIds] = useState<Set<number>>(
    new Set()
  );
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  // Tracks the furthest phase the user has reached — enables forward navigation
  const [maxPhaseReached, setMaxPhaseReached] = useState(currentPhaseIndex);

  // ── Refs ───────────────────────────────────────────────────────────────────
  const isMountedRef = useRef(true);
  const lastGenerationParamsRef =
    useRef<GenerateSimilarProductsFeedParams | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Always-current pagination ref used inside handleLoadMore
  const paginationRef = useRef(pagination);
  const containerRef = useRef<HTMLDivElement>(null);

  const HISTORY_STORAGE_KEY = `curation_replacement_history_${id || 'new'}`;

  // ── Mount / unmount ────────────────────────────────────────────────────────
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  // Keep paginationRef current so handleLoadMore always reads the latest page
  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  // ── Scroll-to-top button visibility ───────────────────────────────────────
  useEffect(() => {
    // Walk up from the container to find the actual scrollable ancestor
    const findScrollable = (el: HTMLElement): HTMLElement | null => {
      let node: HTMLElement | null = el.parentElement;
      while (node) {
        const { overflowY } = window.getComputedStyle(node);
        if (overflowY === 'auto' || overflowY === 'scroll') return node;
        node = node.parentElement;
      }
      return null;
    };
    if (!containerRef.current) return;
    const scrollable = findScrollable(containerRef.current);
    if (!scrollable) return;
    const onScroll = (): void => {
      setShowScrollToTop(scrollable.scrollTop > 300);
    };
    scrollable.addEventListener('scroll', onScroll, { passive: true });
    return () => scrollable.removeEventListener('scroll', onScroll);
  }, []);

  // ── Load replacement history from localStorage ─────────────────────────────
  useEffect(() => {
    try {
      const stored = localStorage.getItem(HISTORY_STORAGE_KEY);
      if (stored) setReplacementHistory(JSON.parse(stored));
    } catch {
      localStorage.removeItem(HISTORY_STORAGE_KEY);
    }
  }, [HISTORY_STORAGE_KEY]);

  // ── Persist replacement history (debounced) ────────────────────────────────
  useEffect(() => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      try {
        const limited: Record<number, Product[]> = {};
        Object.keys(replacementHistory).forEach((k) => {
          const idx = Number(k);
          if (replacementHistory[idx]?.length)
            limited[idx] = replacementHistory[idx].slice(-10);
        });
        localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(limited));
      } catch (err) {
        if (err instanceof DOMException && err.name === 'QuotaExceededError') {
          localStorage.removeItem(HISTORY_STORAGE_KEY);
        }
      }
    }, REPLACEMENT_HISTORY_SAVE_DEBOUNCE_MS);
  }, [replacementHistory, HISTORY_STORAGE_KEY]);

  // ── Load curation for edit mode ────────────────────────────────────────────
  useEffect(() => {
    if (!isEditMode || !id) return;
    const load = async (): Promise<void> => {
      try {
        setError(null);
        const curation = await fetchCurationById(id);
        updateMetadata('title', curation.title);
        updateMetadata('description', curation.description || '');
        updateMetadata('curator', curation.curatorName || '');
        updateMetadata('widgetImage', curation.widgetImage || '');
        updateMetadata('curationImage', curation.curationImage || '');
        updateMetadata('imageTitle', curation.imageTitle || '');
        updateMetadata(
          'isHomepageCuration',
          String(curation.isHomepageCuration || false)
        );
        setExistingProducts(curation.products);
        setUsedReplacements(new Map());
        setReplacementHistory({});
        setPhase(CurationPhase.UPLOAD);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load curation'
        );
      }
    };
    load();
  }, [id, isEditMode, updateMetadata, setExistingProducts, setPhase, setError]);

  // ── Monitor batch upload progress ──────────────────────────────────────────
  useEffect(() => {
    if (!batchUploadInfo || !batches[batchUploadInfo.documentId]) return;
    const batch = batches[batchUploadInfo.documentId];
    const progress = Math.round(
      (batch.processedCount / batch.totalCount) * 100
    );
    uploadedImages.forEach((_, index) =>
      updateImageUploadProgress(index, progress)
    );
    if (batch.status === 'failed') {
      setError(batch.error || 'Batch upload failed');
      setBatchUploadInfo(null);
      setIsUploading(false);
    }
  }, [
    batches,
    batchUploadInfo,
    uploadedImages,
    updateImageUploadProgress,
    setError,
  ]);

  // ── Fetch filter options when entering SELECT phase ────────────────────────
  useEffect(() => {
    if (currentPhase !== CurationPhase.SELECT) return;
    if (cachedFilterOptions) {
      setFilterOptions(cachedFilterOptions);
      return;
    }
    fetchInternalCurationFilters()
      .then((data) => {
        const opts = {
          brands: data.brands?.options || [],
          marketplaces: data.marketplaces?.options || [],
        };
        cachedFilterOptions = opts;
        if (isMountedRef.current) setFilterOptions(opts);
      })
      .catch(() => {
        // filters are optional — silently ignore
      });
  }, [currentPhase]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleImagesSelected = useCallback(
    (files: File[]) => addImages(files),
    [addImages]
  );

  const handleUploadAndProceed = useCallback(async (): Promise<void> => {
    setError(null);
    const filesToUpload = uploadedImages
      .filter((img) => !img.url)
      .map((img) => img.file);

    if (filesToUpload.length === 0) {
      setPhase(CurationPhase.SELECT);
      return;
    }

    setIsUploading(true);

    try {
      const response = await uploadImages(filesToUpload);

      if (
        'batchId' in response &&
        'status' in response &&
        response.status === 'processing'
      ) {
        const docId = response.documentId || response.batchId;
        setBatchUploadInfo({
          documentId: docId,
          totalCount: response.totalCount || filesToUpload.length,
        });

        pollIntervalRef.current = setInterval(async () => {
          try {
            const urls = await getUploadedImageUrls(
              docId,
              response.firestoreCollection || 'screenshot_batches'
            );
            if (urls.length > 0 && isMountedRef.current) {
              if (pollIntervalRef.current)
                clearInterval(pollIntervalRef.current);
              if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
              setBatchUploadInfo(null);
              urls.forEach((url, idx) => {
                updateImageUrl(idx, url);
                updateImageUploadProgress(idx, 100);
              });
              setIsUploading(false);
              setPhase(CurationPhase.SELECT);
            }
          } catch {
            // continue polling
          }
        }, 3000);

        pollTimeoutRef.current = setTimeout(() => {
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
          if (isMountedRef.current) {
            setBatchUploadInfo(null);
            setIsUploading(false);
            setError('Upload timeout. Please try again.');
          }
        }, 120000);
      } else if ('urls' in response && Array.isArray(response.urls)) {
        response.urls.forEach((url: string, idx: number) => {
          updateImageUrl(idx, url);
          updateImageUploadProgress(idx, 100);
        });
        setIsUploading(false);
        setPhase(CurationPhase.SELECT);
      } else if ('url' in response) {
        updateImageUrl(0, response.url);
        updateImageUploadProgress(0, 100);
        setIsUploading(false);
        setPhase(CurationPhase.SELECT);
      } else {
        setIsUploading(false);
        setPhase(CurationPhase.SELECT);
      }
    } catch {
      setError('Failed to upload images. Please try again.');
      setIsUploading(false);
    }
  }, [
    uploadedImages,
    updateImageUrl,
    updateImageUploadProgress,
    setPhase,
    setError,
  ]);

  const handleGenerateFeed = useCallback(async (): Promise<void> => {
    const imageUrls = uploadedImages
      .filter((img) => img.url)
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      .map((img) => img.url!);

    if (!imageUrls.length) {
      setError(
        'No uploaded images available. Please go back and upload images first.'
      );
      return;
    }

    clearFeed();
    setIsGeneratingFeed(true);
    setError(null);

    const params: GenerateSimilarProductsFeedParams = {
      imageUrls,
      page: 1,
      ...(filters.minPrice ? { minPrice: Number(filters.minPrice) } : {}),
      ...(filters.maxPrice ? { maxPrice: Number(filters.maxPrice) } : {}),
      ...(sale ? { sale: true } : {}),
      ...(filters.brands.length ? { brands: filters.brands } : {}),
      ...(filters.marketplaces.length
        ? { marketplaces: filters.marketplaces }
        : {}),
    };
    lastGenerationParamsRef.current = params;

    try {
      const response = await generateSimilarProductsFeed(params);
      const responseIds = new Set(response.products.map((p) => p.id));
      const orphanedSelected = selectedProducts.filter(
        (p) => !responseIds.has(p.id)
      );
      setGeneratedFeed([...response.products, ...orphanedSelected]);
      if (response.pagination) {
        setPagination({
          page: response.pagination.currentPage,
          hasMore:
            response.pagination.currentPage < response.pagination.totalPages,
          totalPages: response.pagination.totalPages,
          totalItems: response.pagination.totalItems,
        });
      } else {
        setPagination(null);
      }
    } catch {
      setError('Failed to generate feed. Please try again.');
    } finally {
      setIsGeneratingFeed(false);
    }
  }, [
    uploadedImages,
    filters,
    sale,
    selectedProducts,
    clearFeed,
    setIsGeneratingFeed,
    setError,
    setGeneratedFeed,
    setPagination,
  ]);

  // Called by DoubleColumnProductList's internal scroll handler
  const handleLoadMore = useCallback(async (): Promise<void> => {
    const currentPagination = paginationRef.current;
    if (!currentPagination?.hasMore || !lastGenerationParamsRef.current) return;
    setIsLoadingMore(true);
    const nextPage = currentPagination.page + 1;
    try {
      const response = await generateSimilarProductsFeed({
        ...lastGenerationParamsRef.current,
        page: nextPage,
      });
      appendToFeed(response.products);
      if (response.pagination) {
        setPagination({
          page: response.pagination.currentPage,
          hasMore:
            response.pagination.currentPage < response.pagination.totalPages,
          totalPages: response.pagination.totalPages,
          totalItems: response.pagination.totalItems,
        });
      }
    } catch (err) {
      console.warn('Load more failed:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [appendToFeed, setPagination]);

  const handleProceedToReview = useCallback((): void => {
    initReviewProducts();
    setUsedReplacements(new Map());
    setReplacementHistory({});
    setSelectedRanks(new Map());
    setPhase(CurationPhase.REVIEW);
  }, [initReviewProducts, setPhase]);

  const handleRemoveReviewProduct = useCallback(
    (productId: number): void => {
      removeReviewProduct(productId);
      setSelectedRanks((prev) => {
        const m = new Map(prev);
        m.delete(productId);
        return m;
      });
    },
    [removeReviewProduct]
  );

  const handleReplaceProduct = useCallback(
    async (product: Product): Promise<void> => {
      setReplacingProductId(product.id);
      setError(null);
      try {
        const publicImageUrl = updatedImageUrl(product.imageUrl);
        const response = await fetchSimilarProducts({
          imageUrl: publicImageUrl,
          minPrice: filters.minPrice ? Number(filters.minPrice) : undefined,
          maxPrice: filters.maxPrice ? Number(filters.maxPrice) : undefined,
          page: 1,
          ...(sale ? { sale: true } : {}),
        });

        const existingIds = new Set(reviewProducts.map((p) => p.id));
        const previouslyUsed =
          usedReplacements.get(product.id) || new Set<number>();
        const newProduct = response.products
          .slice(0, 20)
          .find((p) => !existingIds.has(p.id) && !previouslyUsed.has(p.id));

        if (!newProduct) {
          setError('No new similar product found.');
          return;
        }

        const productIndex = reviewProducts.findIndex(
          (p) => p.id === product.id
        );
        if (productIndex !== -1) {
          setReplacementHistory((prev) => ({
            ...prev,
            [productIndex]: [...(prev[productIndex] || []), product],
          }));
          replaceReviewProduct(product.id, newProduct);
          setUsedReplacements((prev) => {
            const m = new Map(prev);
            const used = new Set(m.get(product.id) || []);
            used.add(newProduct.id);
            m.set(product.id, used);
            return m;
          });
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to fetch replacement'
        );
      } finally {
        setReplacingProductId(null);
      }
    },
    [
      reviewProducts,
      filters.minPrice,
      filters.maxPrice,
      sale,
      usedReplacements,
      replaceReviewProduct,
      setError,
    ]
  );

  const handleRestoreProduct = useCallback(
    (currentProduct: Product): void => {
      const productIndex = reviewProducts.findIndex(
        (p) => p.id === currentProduct.id
      );
      if (productIndex === -1) return;
      const history = replacementHistory[productIndex];
      if (!history?.length) return;

      const previousProduct = history[history.length - 1];
      replaceReviewProduct(currentProduct.id, previousProduct);
      setReplacementHistory((prev) => {
        const next = { ...prev };
        const stack = [...history];
        stack.pop();
        if (stack.length === 0) delete next[productIndex];
        else next[productIndex] = stack;
        return next;
      });
    },
    [reviewProducts, replacementHistory, replaceReviewProduct]
  );

  const remapReplacementHistory = useCallback(
    (
      oldProducts: Product[],
      newProducts: Product[]
    ): Record<number, Product[]> => {
      const idToNewIndex = new Map<number, number>();
      newProducts.forEach((p, i) => idToNewIndex.set(p.id, i));
      const remapped: Record<number, Product[]> = {};
      Object.keys(replacementHistory).forEach((k) => {
        const oldIdx = Number(k);
        const prodId = oldProducts[oldIdx]?.id;
        if (prodId !== undefined) {
          const newIdx = idToNewIndex.get(prodId);
          if (newIdx !== undefined && replacementHistory[oldIdx]) {
            remapped[newIdx] = replacementHistory[oldIdx];
          }
        }
      });
      return remapped;
    },
    [replacementHistory]
  );

  const handleDragStart = useCallback((event: DragStartEvent): void => {
    setActiveDragId(event.active.id as number);
  }, []);

  const handleDragCancel = useCallback((): void => {
    setActiveDragId(null);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent): void => {
      setActiveDragId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const activeId = active.id as number;
      const overId = over.id as number;
      const oldProducts = useCreateCurationStore.getState().reviewProducts;
      const isGroupDrag = selectedRanks.size > 1 && selectedRanks.has(activeId);

      let reordered: Product[];

      if (isGroupDrag) {
        if (selectedRanks.has(overId)) return;
        const ranked: Product[] = [];
        const remaining: Product[] = [];
        for (const p of oldProducts) {
          (selectedRanks.has(p.id) ? ranked : remaining).push(p);
        }
        ranked.sort(
          (a, b) =>
            (selectedRanks.get(a.id) ?? 0) - (selectedRanks.get(b.id) ?? 0)
        );
        const insertAt = remaining.findIndex((p) => p.id === overId);
        if (insertAt === -1) return;
        reordered = [
          ...remaining.slice(0, insertAt),
          ...ranked,
          ...remaining.slice(insertAt),
        ];
        setSelectedRanks(new Map());
      } else {
        const oldIndex = oldProducts.findIndex((p) => p.id === activeId);
        const newIndex = oldProducts.findIndex((p) => p.id === overId);
        if (oldIndex === -1 || newIndex === -1) return;
        reordered = arrayMove(oldProducts, oldIndex, newIndex);
        if (selectedRanks.has(activeId)) {
          setSelectedRanks((prev) => {
            const m = new Map(prev);
            m.delete(activeId);
            return m;
          });
        }
      }

      useCreateCurationStore.getState().setReviewProducts(reordered);
      setReplacementHistory(remapReplacementHistory(oldProducts, reordered));
    },
    [remapReplacementHistory, selectedRanks]
  );

  const handleToggleRankSelection = useCallback((productId: number): void => {
    setSelectedRanks((prev) => {
      const m = new Map(prev);
      if (m.has(productId)) {
        m.delete(productId);
      } else {
        const maxRank = m.size > 0 ? Math.max(...Array.from(m.values())) : 0;
        m.set(productId, maxRank + 1);
      }
      return m;
    });
  }, []);

  const handleMoveToTop = useCallback((): void => {
    if (!selectedRanks.size) return;
    const oldProducts = [...reviewProducts];
    const ranked: Product[] = [];
    const unranked: Product[] = [];
    reviewProducts.forEach((p) => {
      if (selectedRanks.has(p.id)) ranked.push(p);
      else unranked.push(p);
    });
    ranked.sort(
      (a, b) => (selectedRanks.get(a.id) || 0) - (selectedRanks.get(b.id) || 0)
    );
    const newProducts = [...ranked, ...unranked];
    setReviewProducts(newProducts);
    setReplacementHistory(remapReplacementHistory(oldProducts, newProducts));
    setSelectedRanks(new Map());
  }, [
    reviewProducts,
    selectedRanks,
    setReviewProducts,
    remapReplacementHistory,
  ]);

  // Fetches products from the supplied curation IDs and stages them in a
  // preview list — the user picks which to actually merge via the inline UI.
  const handleImportFromExistingCuration = useCallback(
    async (target: 'select' | 'review'): Promise<void> => {
      if (!existingCurationId.trim()) {
        setError('Please enter a curation ID');
        return;
      }
      setIsImportingProducts(true);
      setError(null);
      try {
        const ids = existingCurationId
          .split(/[,\n]/)
          .map((s) => s.trim())
          .filter(Boolean);
        const curations = await Promise.all(
          ids.map((cid) => fetchCurationById(cid).catch(() => null))
        );
        const allProducts: Product[] = curations.flatMap(
          (c) => c?.products || []
        );
        const pool = target === 'review' ? reviewProducts : selectedProducts;
        const existingIds = new Set(pool.map((p) => p.id));
        const seen = new Set<number>();
        const fresh = allProducts.filter((p) => {
          if (existingIds.has(p.id) || seen.has(p.id)) return false;
          seen.add(p.id);
          return true;
        });
        if (!fresh.length) {
          setError('No new products found in those curations.');
          return;
        }
        setImportPreview({ products: fresh, target });
        setImportSelectedIds(new Set());
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Import failed');
      } finally {
        setIsImportingProducts(false);
      }
    },
    [existingCurationId, reviewProducts, selectedProducts, setError]
  );

  const handleToggleImportSelection = useCallback((productId: number): void => {
    setImportSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  }, []);

  const handleSelectAllImported = useCallback((): void => {
    if (!importPreview) return;
    setImportSelectedIds(new Set(importPreview.products.map((p) => p.id)));
  }, [importPreview]);

  const handleClearImportedSelection = useCallback((): void => {
    setImportSelectedIds(new Set());
  }, []);

  const handleCancelImportPreview = useCallback((): void => {
    setImportPreview(null);
    setImportSelectedIds(new Set());
  }, []);

  const handleConfirmImport = useCallback((): void => {
    if (!importPreview) return;
    const chosen = importPreview.products.filter((p) =>
      importSelectedIds.has(p.id)
    );
    if (!chosen.length) {
      setError('Select at least one product to import.');
      return;
    }
    if (importPreview.target === 'review') {
      setReviewProducts([...reviewProducts, ...chosen]);
    } else {
      const existingIds = new Set(selectedProducts.map((p) => p.id));
      const additions = chosen.filter((p) => !existingIds.has(p.id));
      setSelectedProducts([...selectedProducts, ...additions]);
    }
    setImportPreview(null);
    setImportSelectedIds(new Set());
    setExistingCurationId('');
    setImportFromExistingCuration(false);
  }, [
    importPreview,
    importSelectedIds,
    reviewProducts,
    selectedProducts,
    setReviewProducts,
    setSelectedProducts,
    setError,
  ]);

  const validateForm = useCallback((): boolean => {
    const errs: Record<string, string> = {};
    if (!metadata.title.trim()) errs.title = 'Title is required';
    if (!metadata.description.trim())
      errs.description = 'Description is required';
    if (!metadata.curator.trim()) errs.curator = 'Curator name is required';
    setValidationErrors(errs);
    return Object.keys(errs).length === 0;
  }, [metadata]);

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (!validateForm()) return;
    setError(null);
    setIsSubmitting(true);
    try {
      const productIds = reviewProducts.map((p) => p.id);
      if (isEditMode && id) {
        await updateCuration(id, {
          title: metadata.title,
          description: metadata.description,
          curatorName: metadata.curator,
          myntraProductIds: productIds,
          widgetImage: metadata.widgetImage,
          curationImage: metadata.curationImage,
          imageTitle: metadata.imageTitle,
          isHomepageCuration: metadata.isHomepageCuration,
        });
        navigate(`/curations/v2/${id}`);
      } else {
        const response = await createCuration({
          title: metadata.title,
          description: metadata.description,
          curatorName: metadata.curator,
          myntraProductIds: productIds,
          widgetImage: metadata.widgetImage,
          curationImage: metadata.curationImage,
          imageTitle: metadata.imageTitle,
          isHomepageCuration: metadata.isHomepageCuration,
        });
        navigate(`/curations/v2/${response.id}`);
      }
      reset();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : isEditMode
            ? 'Failed to update curation'
            : 'Failed to create curation'
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    validateForm,
    reviewProducts,
    metadata,
    navigate,
    setError,
    setIsSubmitting,
    isEditMode,
    id,
    reset,
  ]);

  const handleBack = useCallback((): void => {
    if (
      isGeneratingFeed ||
      isSubmitting ||
      replacingProductId !== null ||
      isUploading
    )
      return;
    if (currentPhase === CurationPhase.UPLOAD) {
      if (
        uploadedImages.length > 0 ||
        reviewProducts.length > 0 ||
        metadata.title
      ) {
        if (
          !window.confirm(
            'You have unsaved changes. Are you sure you want to leave?'
          )
        )
          return;
      }
      navigate(-1);
    } else if (currentPhase === CurationPhase.SELECT) {
      setPhase(CurationPhase.UPLOAD);
    } else if (currentPhase === CurationPhase.REVIEW) {
      setPhase(CurationPhase.SELECT);
    } else {
      navigate(-1);
    }
  }, [
    currentPhase,
    navigate,
    setPhase,
    isGeneratingFeed,
    isSubmitting,
    replacingProductId,
    isUploading,
    uploadedImages.length,
    reviewProducts.length,
    metadata.title,
  ]);

  const handleForward = useCallback((): void => {
    if (
      isGeneratingFeed ||
      isSubmitting ||
      replacingProductId !== null ||
      isUploading
    )
      return;
    if (currentPhaseIndex >= maxPhaseReached) return;
    setPhase(PHASE_ORDER[currentPhaseIndex + 1]);
  }, [
    currentPhaseIndex,
    maxPhaseReached,
    isGeneratingFeed,
    isSubmitting,
    replacingProductId,
    isUploading,
    setPhase,
  ]);

  const handleScrollToTop = useCallback((): void => {
    const findScrollable = (el: HTMLElement): HTMLElement | null => {
      let node: HTMLElement | null = el.parentElement;
      while (node) {
        const { overflowY } = window.getComputedStyle(node);
        if (overflowY === 'auto' || overflowY === 'scroll') return node;
        node = node.parentElement;
      }
      return null;
    };
    if (!containerRef.current) return;
    const scrollable = findScrollable(containerRef.current);
    scrollable?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 8 },
    })
  );

  const productIds = useMemo(
    () => reviewProducts.map((p) => p.id),
    [reviewProducts]
  );

  const isGroupDragActive =
    activeDragId !== null &&
    selectedRanks.has(activeDragId) &&
    selectedRanks.size > 1;

  const activeDragProduct = useMemo(
    () =>
      activeDragId !== null
        ? (reviewProducts.find((p) => p.id === activeDragId) ?? null)
        : null,
    [activeDragId, reviewProducts]
  );

  const renderSelectProduct = useCallback(
    (product: Product): JSX.Element => {
      const isSelected = isProductSelected(product.id);
      return (
        <div
          className={`${styles.productWrapper} ${isSelected ? styles.productSelected : ''}`}
          onClick={() => toggleProductSelection(product)}
        >
          <ProductCard product={product} disableNavigation={true} />
          <div
            className={`${styles.selectionButton} ${isSelected ? styles.selected : ''}`}
            style={{ pointerEvents: 'none' }}
          >
            {isSelected ? '✓' : '+'}
          </div>
        </div>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isProductSelected, toggleProductSelection, selectedProducts]
  );

  // Shared Import UI rendered in both SELECT and REVIEW phases
  const renderImportSection = (target: 'select' | 'review'): JSX.Element => {
    const previewActive = importPreview?.target === target;
    return (
      <div className={styles.importSection}>
        <div className={styles.saleToggleContainer}>
          <label className={styles.toggleLabel}>
            <input
              type="checkbox"
              className={styles.toggleInput}
              checked={importFromExistingCuration}
              onChange={(e) => {
                setImportFromExistingCuration(e.target.checked);
                if (!e.target.checked) handleCancelImportPreview();
              }}
            />
            <span className={styles.toggleSlider} />
            <span className={styles.toggleText}>
              Import from Existing Curation
            </span>
          </label>
        </div>
        {importFromExistingCuration && !previewActive && (
          <div className={styles.importInputRow}>
            <div
              className={styles.formGroup}
              style={{ marginBottom: 0, flex: 1 }}
            >
              <label className={styles.label}>Curation ID(s)</label>
              <textarea
                className={styles.textarea}
                placeholder="Enter curation IDs (comma or newline separated)"
                value={existingCurationId}
                rows={2}
                onChange={(e) => setExistingCurationId(e.target.value)}
              />
            </div>
            <button
              className={styles.actionButton}
              type="button"
              disabled={!existingCurationId.trim() || isImportingProducts}
              onClick={() => handleImportFromExistingCuration(target)}
            >
              {isImportingProducts ? 'Importing...' : 'Import'}
            </button>
          </div>
        )}
        {previewActive && importPreview && (
          <div className={styles.importPreview}>
            <div className={styles.importPreviewHeader}>
              <span>
                {importPreview.products.length} found •{' '}
                <strong>{importSelectedIds.size} selected</strong>
              </span>
              <div className={styles.importPreviewHeaderButtons}>
                <button
                  type="button"
                  className={styles.filterChip}
                  onClick={handleSelectAllImported}
                >
                  Select all
                </button>
                <button
                  type="button"
                  className={styles.filterChip}
                  onClick={handleClearImportedSelection}
                >
                  Clear
                </button>
              </div>
            </div>
            <div className={styles.importPreviewGrid}>
              {importPreview.products.map((p) => {
                const isChecked = importSelectedIds.has(p.id);
                return (
                  <div
                    key={p.id}
                    className={`${styles.productWrapper} ${isChecked ? styles.productSelected : ''}`}
                    onClick={() => handleToggleImportSelection(p.id)}
                  >
                    <ProductCard product={p} disableNavigation={true} />
                    <div
                      className={`${styles.selectionButton} ${isChecked ? styles.selected : ''}`}
                      style={{ pointerEvents: 'none' }}
                    >
                      {isChecked ? '✓' : '+'}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className={styles.importPreviewActions}>
              <button
                type="button"
                className={`${styles.actionButton} ${styles.secondary}`}
                onClick={handleCancelImportPreview}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`${styles.actionButton} ${styles.primary}`}
                disabled={importSelectedIds.size === 0}
                onClick={handleConfirmImport}
              >
                Add {importSelectedIds.size} selected
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const uploadedImageUrls = useMemo(
    () =>
      uploadedImages
        .filter((img) => img.url)
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        .map((img) => img.url!),
    [uploadedImages]
  );

  // Advance maxPhaseReached whenever the user moves forward
  useEffect(() => {
    setMaxPhaseReached((prev) => Math.max(prev, currentPhaseIndex));
  }, [currentPhaseIndex]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className={styles.container} ref={containerRef}>
      {/* Header */}
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBack}>
          <img src={backButtonDark} alt="Back" className={styles.backIcon} />
        </button>
        <div className={styles.headerTitle}>
          {isEditMode ? 'Edit Curation' : 'Create Curation'}
        </div>
        {currentPhaseIndex < maxPhaseReached ? (
          <button className={styles.forwardButton} onClick={handleForward}>
            <img
              src={backButtonDark}
              alt="Forward"
              className={styles.forwardIcon}
            />
          </button>
        ) : (
          <div style={{ width: '24px' }} />
        )}
      </div>

      {/* Phase Indicator */}
      <div className={styles.phaseIndicator}>
        {PHASE_ORDER.map((phase, i) => (
          <div
            key={phase}
            className={`${styles.phaseStep} ${currentPhaseIndex === i ? styles.active : ''} ${currentPhaseIndex > i ? styles.completed : ''}`}
          >
            <div className={styles.phaseNumber}>{i + 1}</div>
            <div className={styles.phaseLabel}>{PHASE_LABELS[phase]}</div>
          </div>
        ))}
      </div>

      {/* Move to Top (REVIEW step) */}
      {selectedRanks.size > 0 && currentPhase === CurationPhase.REVIEW && (
        <div className={styles.moveToTopContainer}>
          <button
            className={styles.moveToTopButton}
            type="button"
            title="Move selected to top (or drag any selected product to drop the group anywhere)"
            onClick={handleMoveToTop}
          >
            <span style={{ fontSize: '1.5rem' }}>⬆</span>
          </button>
        </div>
      )}

      {/* Error Banner */}
      {error && (
        <div className={styles.content}>
          <div className={styles.errorBanner}>
            <div className={styles.errorIcon}>⚠️</div>
            <div className={styles.errorMessage}>{error}</div>
          </div>
        </div>
      )}

      <div className={styles.content}>
        {/* ── Phase 1: Upload ─────────────────────────────────────────────── */}
        {currentPhase === CurationPhase.UPLOAD && (
          <div>
            <h1 className={styles.phaseTitle}>Upload Screenshots</h1>
            <p className={styles.phaseDescription}>
              Upload fashion screenshots to generate a product feed
            </p>

            <ImageUploadZone
              uploadedImages={uploadedImages}
              maxImages={70}
              disabled={isUploading}
              onImagesSelected={handleImagesSelected}
              onRemoveImage={removeImage}
            />

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                alignItems: 'center',
                width: '100%',
                maxWidth: '400px',
                margin: '2rem auto 0',
              }}
            >
              {uploadedImages.length > 0 && (
                <button
                  className={styles.generateButton}
                  style={{ margin: 0, width: '100%' }}
                  disabled={isUploading || !!batchUploadInfo}
                  onClick={handleUploadAndProceed}
                >
                  {batchUploadInfo ? (
                    <>
                      {batches[batchUploadInfo.documentId]
                        ? `Uploading... ${batches[batchUploadInfo.documentId].processedCount}/${batchUploadInfo.totalCount}`
                        : 'Processing...'}
                    </>
                  ) : isUploading ? (
                    <>
                      <div className={styles.spinner} />
                      Uploading...
                    </>
                  ) : (
                    `Upload & Continue (${uploadedImages.length} images)`
                  )}
                </button>
              )}

              {/* Edit mode: skip straight to REVIEW if we have existing products */}
              {isEditMode && existingProducts.length > 0 && (
                <>
                  <button
                    className={`${styles.actionButton} ${styles.secondary}`}
                    style={{ width: '100%' }}
                    onClick={() => {
                      initReviewProducts();
                      setPhase(CurationPhase.REVIEW);
                    }}
                  >
                    Skip to Review ({existingProducts.length} products)
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Phase 2: Select ──────────────────────────────────────────────── */}
        {currentPhase === CurationPhase.SELECT && (
          <div>
            <h1 className={styles.phaseTitle}>Select Products</h1>
            <p className={styles.phaseDescription}>
              Apply filters, generate a feed, and select products for your
              curation
            </p>

            {/* Filters Bar */}
            <div className={styles.filtersSection}>
              {/* Price Range */}
              <div className={styles.priceRangeContainer}>
                <div
                  className={styles.formGroup}
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <label className={styles.label}>Min Price</label>
                  <input
                    type="number"
                    className={styles.input}
                    placeholder="Min"
                    value={filters.minPrice}
                    min="0"
                    disabled={isGeneratingFeed}
                    onChange={(e) => setFilters({ minPrice: e.target.value })}
                  />
                </div>
                <div
                  className={styles.formGroup}
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <label className={styles.label}>Max Price</label>
                  <input
                    type="number"
                    className={styles.input}
                    placeholder="Max"
                    value={filters.maxPrice}
                    min="0"
                    disabled={isGeneratingFeed}
                    onChange={(e) => setFilters({ maxPrice: e.target.value })}
                  />
                </div>
              </div>

              {/* Sale Toggle */}
              <div className={styles.saleToggleContainer}>
                <label className={styles.toggleLabel}>
                  <input
                    type="checkbox"
                    className={styles.toggleInput}
                    checked={sale}
                    disabled={isGeneratingFeed}
                    onChange={(e) => setSale(e.target.checked)}
                  />
                  <span className={styles.toggleSlider} />
                  <span className={styles.toggleText}>Sale Only</span>
                </label>
              </div>

              {/* Marketplaces */}
              {filterOptions.marketplaces.length > 0 && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>Marketplaces</label>
                  <div className={styles.filterChips}>
                    {filterOptions.marketplaces.map((opt) => {
                      const isActive = filters.marketplaces.includes(opt.value);
                      const marketplaceLogo =
                        opt.label in MAKRKETPLACE_IMAGE_MAP
                          ? MAKRKETPLACE_IMAGE_MAP[opt.label as MarketplaceKey]
                          : undefined;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          className={`${styles.filterChip} ${isActive ? styles.filterChipActive : ''}`}
                          onClick={() =>
                            setFilters({
                              marketplaces: isActive
                                ? filters.marketplaces.filter(
                                    (v) => v !== opt.value
                                  )
                                : [...filters.marketplaces, opt.value],
                            })
                          }
                        >
                          {marketplaceLogo && (
                            <img
                              src={marketplaceLogo}
                              alt=""
                              aria-hidden="true"
                              className={styles.marketplaceChipLogo}
                            />
                          )}
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Brands */}
              {filterOptions.brands.length > 0 && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>Brands</label>
                  <div className={styles.filterChips}>
                    {filterOptions.brands.map((opt) => {
                      const isActive = filters.brands.includes(opt.value);
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          className={`${styles.filterChip} ${isActive ? styles.filterChipActive : ''}`}
                          onClick={() =>
                            setFilters({
                              brands: isActive
                                ? filters.brands.filter((v) => v !== opt.value)
                                : [...filters.brands, opt.value],
                            })
                          }
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Import from existing curation (SELECT step) */}
            {renderImportSection('select')}

            {/* Generate Feed Button + Proceed CTA */}
            <div className={styles.generateRow}>
              <button
                className={styles.generateButton}
                disabled={isGeneratingFeed || !uploadedImageUrls.length}
                onClick={handleGenerateFeed}
              >
                {isGeneratingFeed ? (
                  <>
                    <div className={styles.spinner} />
                    Generating...
                  </>
                ) : (
                  'Generate Feed'
                )}
              </button>
              {selectedProducts.length > 0 && (
                <button
                  className={styles.actionButton}
                  onClick={handleProceedToReview}
                >
                  Proceed to Review ({selectedProducts.length} selected)
                </button>
              )}
            </div>

            {/* Product Feed with Checkboxes */}
            {generatedFeed.length > 0 && (
              <div>
                <div
                  className={styles.productCount}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span>
                    {generatedFeed.length} products
                    {pagination ? ` of ${pagination.totalItems}` : ''}
                  </span>
                  <span style={{ fontWeight: 600 }}>
                    {selectedProducts.length} selected
                  </span>
                </div>

                <DoubleColumnProductList
                  products={generatedFeed}
                  isLoading={isLoadingMore}
                  hasMore={pagination?.hasMore ?? false}
                  renderProductItemOverride={renderSelectProduct}
                  onLoadMore={handleLoadMore}
                />
              </div>
            )}
          </div>
        )}

        {/* ── Phase 3: Review ──────────────────────────────────────────────── */}
        {currentPhase === CurationPhase.REVIEW && (
          <div>
            <h1 className={styles.phaseTitle}>Review Products</h1>
            <p className={styles.phaseDescription}>
              Reorder, replace, or remove products. Select multiple to move them
              to the top — or drag any selected product to drop the whole group
              anywhere in the list.
            </p>

            {reviewProducts.length > 0 ? (
              <>
                <div className={styles.productCount}>
                  {reviewProducts.length} products in feed
                </div>

                <div className={styles.reviewActions}>
                  <button
                    className={`${styles.actionButton} ${styles.secondary}`}
                    onClick={() => {
                      setReviewProducts([]);
                      setSelectedRanks(new Map());
                      setReplacementHistory({});
                      setUsedReplacements(new Map());
                    }}
                  >
                    Clear All
                  </button>
                  <button
                    className={`${styles.actionButton} ${styles.primary}`}
                    onClick={() => setPhase(CurationPhase.FINALIZE)}
                  >
                    Proceed to Finalize
                  </button>
                </div>

                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCorners}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onDragCancel={handleDragCancel}
                >
                  <SortableContext
                    items={productIds}
                    strategy={rectSortingStrategy}
                  >
                    <div className={styles.productGrid}>
                      {reviewProducts.map((product, index) => {
                        const isReplacing = replacingProductId === product.id;
                        const hasHistory = !!replacementHistory[index]?.length;
                        const historyCount =
                          replacementHistory[index]?.length || 0;
                        const rank = selectedRanks.get(product.id);
                        const isSelected = rank !== undefined;
                        const isInDragGroup = isGroupDragActive && isSelected;
                        return (
                          <DraggableProductItem
                            key={product.id}
                            product={product}
                            isReplacing={isReplacing}
                            hasHistory={hasHistory}
                            historyCount={historyCount}
                            rank={rank}
                            isSelected={isSelected}
                            isInDragGroup={isInDragGroup}
                            onRestore={handleRestoreProduct}
                            onReplace={handleReplaceProduct}
                            onRemove={handleRemoveReviewProduct}
                            onToggleSelection={handleToggleRankSelection}
                          />
                        );
                      })}
                    </div>
                  </SortableContext>
                  <DragOverlay
                    dropAnimation={{
                      duration: 220,
                      easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
                      sideEffects: defaultDropAnimationSideEffects({
                        styles: { active: { opacity: '0.4' } },
                      }),
                    }}
                  >
                    {activeDragProduct ? (
                      <div className={styles.dragOverlayCard}>
                        <ProductCard
                          product={activeDragProduct}
                          disableNavigation={true}
                        />
                      </div>
                    ) : null}
                  </DragOverlay>
                </DndContext>

                {/* Import from existing curation (REVIEW step) */}
                {renderImportSection('review')}
              </>
            ) : (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateIcon}>📦</div>
                <div className={styles.emptyStateText}>
                  No products yet. Import from an existing curation or go back
                  to select products.
                </div>
                {renderImportSection('review')}
              </div>
            )}
          </div>
        )}

        {/* ── Phase 4: Finalize ────────────────────────────────────────────── */}
        {currentPhase === CurationPhase.FINALIZE && (
          <div className={styles.finalizePhase}>
            <h1 className={styles.phaseTitle}>Finalize Curation</h1>
            <p className={styles.phaseDescription}>
              Add metadata and submit your curation
            </p>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Title<span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  className={styles.input}
                  value={metadata.title}
                  placeholder="Enter curation title"
                  onChange={(e) => updateMetadata('title', e.target.value)}
                />
                {validationErrors.title && (
                  <div className={styles.errorText}>
                    {validationErrors.title}
                  </div>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Description<span className={styles.required}>*</span>
                </label>
                <textarea
                  className={styles.textarea}
                  value={metadata.description}
                  placeholder="Enter curation description"
                  onChange={(e) =>
                    updateMetadata('description', e.target.value)
                  }
                />
                {validationErrors.description && (
                  <div className={styles.errorText}>
                    {validationErrors.description}
                  </div>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Curator<span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  className={styles.input}
                  value={metadata.curator}
                  placeholder="Enter curator name"
                  onChange={(e) => updateMetadata('curator', e.target.value)}
                />
                {validationErrors.curator && (
                  <div className={styles.errorText}>
                    {validationErrors.curator}
                  </div>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Widget Image URL</label>
                <input
                  type="url"
                  className={styles.input}
                  value={metadata.widgetImage}
                  placeholder="https://example.com/image.jpg (optional)"
                  onChange={(e) =>
                    updateMetadata('widgetImage', e.target.value)
                  }
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Curation Image URL</label>
                <input
                  type="url"
                  className={styles.input}
                  value={metadata.curationImage}
                  placeholder="https://example.com/image.jpg (optional)"
                  onChange={(e) =>
                    updateMetadata('curationImage', e.target.value)
                  }
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Image Title</label>
                <input
                  type="text"
                  className={styles.input}
                  value={metadata.imageTitle}
                  placeholder="Enter image title (optional)"
                  onChange={(e) => updateMetadata('imageTitle', e.target.value)}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  <input
                    type="checkbox"
                    checked={metadata.isHomepageCuration}
                    style={{ marginRight: '0.5rem' }}
                    onChange={(e) => {
                      updateMetadata(
                        'isHomepageCuration',
                        e.target.checked ? 'true' : 'false'
                      );
                    }}
                  />
                  Homepage Curation
                </label>
              </div>

              <button
                type="submit"
                className={styles.submitButton}
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? isEditMode
                    ? 'Updating Curation...'
                    : 'Creating Curation...'
                  : isEditMode
                    ? 'Update Curation'
                    : 'Create Curation'}
              </button>
            </form>
          </div>
        )}
      </div>

      {/* Scroll to Top FAB */}
      {showScrollToTop && (
        <button
          aria-label="Scroll to top"
          className={styles.scrollToTopButton}
          onClick={handleScrollToTop}
        >
          ↑
        </button>
      )}

      {/* Loading Overlay */}
      {(isGeneratingFeed || isSubmitting || isUploading) && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingContent}>
            <div className={styles.spinner} />
            <div className={styles.loadingText}>
              {isUploading
                ? 'Uploading images...'
                : isGeneratingFeed
                  ? 'Generating product feed...'
                  : isEditMode
                    ? 'Updating curation...'
                    : 'Creating curation...'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateCurationPage;
